import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Heart, Share } from 'lucide-react';
import TermsPDFViewer from './TermsPDFViewer';
import ClientMedicalRecordForm from './ClientMedicalRecordForm';
import { useTermsSignature } from '@/hooks/clients/useTermsSignature';
import { useClientMedicalRecord } from '@/hooks/clients/useClientMedicalRecord';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';

interface ClientTermsAcceptanceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
  client?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    birthDate?: string;
    weightKg?: number;
    heightCm?: number;
  };
  termsUrl: string;
}
const ClientTermsAcceptanceModal: React.FC<ClientTermsAcceptanceModalProps> = ({
  open,
  onOpenChange,
  clientId,
  clientName,
  client,
  termsUrl
}) => {
  const {
    t
  } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isFullScreen = isMobile || isTablet;
  const {
    recordAcceptance,
    isRecording
  } = useTermsSignature();

  const {
    medicalRecord,
    saveMedicalRecord,
    isSaving
  } = useClientMedicalRecord(clientId);

  const handleTermsAcceptance = async (signatureData: string) => {
    console.log('✅ Attempting to record terms acceptance from medical record', {
      clientId,
      signatureDataLength: signatureData.length,
      signatureDataPreview: signatureData.substring(0, 50) + '...'
    });
    try {
      await recordAcceptance({
        clientId,
        signatureData: signatureData
      });
      console.log('✅ Terms acceptance recorded successfully');
      // Small delay to ensure cache invalidation completes before closing modal
    } catch (error) {
      console.error('❌ Failed to record terms acceptance:', error);
      // Error handling is done in the hook
      throw error; // Re-throw to let the medical form handle it
    }
  };

  const handleSharePDF = async () => {
    if (!termsUrl) return;

    try {
      // Check if Web Share API is available before downloading
      const canShareFiles = navigator.canShare && navigator.canShare({ files: [] });
      const canShareText = !!navigator.share;
      
      console.log('📱 Share capabilities:', { canShareFiles, canShareText, termsUrl });

      const bucketName = 'gym-documents'

      const { data: blob, error } = await supabase.storage
        .from(bucketName)
        .download(termsUrl);

      if (error) throw error;
      if (!blob) throw new Error('Downloaded blob is null');

      const fileName = `${clientName} - ${t('gymSettings.terms')}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });

      console.log('✅ PDF fetched successfully for sharing:', termsUrl);
      console.log('📄 PDF file created:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      const subject = `${t('gymSettings.terms')} - ${clientName}`;
      const body = `${t('clients.termsAcceptance.shareText')} ${clientName}`;

      // Try different sharing methods in order of preference
      let shareSuccessful = false;

      // Method 1: Native file sharing (mobile primarily)
      if (canShareFiles) {
        try {
          const shareData = {
            files: [file],
            title: subject,
            text: body,
          };
          
          // Check if we can share this specific data
          if (navigator.canShare(shareData)) {
            await navigator.share(shareData);
            shareSuccessful = true;
          }
        } catch (shareError) {
          console.warn('File sharing failed, trying alternative methods:', shareError);
        }
      }

      // Method 2: Text-only sharing (mobile/desktop)
      if (!shareSuccessful && canShareText) {
        try {
          // Create a temporary URL for the blob
          const fileUrl = URL.createObjectURL(blob);
          await navigator.share({
            title: subject,
            text: body,
            url: fileUrl,
          });
          // Clean up the URL after a delay
          setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
          shareSuccessful = true;
        } catch (shareError) {
          console.warn('Text sharing failed, trying fallback methods:', shareError);
        }
      }

      // Method 3: Email fallback
      if (!shareSuccessful && client?.email) {
        try {
          const mailtoLink = `mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
          window.open(mailtoLink, '_blank');
          shareSuccessful = true;
        } catch (emailError) {
          console.warn('Email sharing failed:', emailError);
        }
      }

      // Method 4: Download/Open fallback (always available)
      if (!shareSuccessful) {
        // Try to open in new tab first, fallback to download
        try {
          const fileUrl = URL.createObjectURL(blob);
          const newWindow = window.open(fileUrl, '_blank');
          if (!newWindow) {
            // Popup blocked, fallback to download
            const a = document.createElement('a');
            a.href = fileUrl;
            a.download = fileName;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }
          // Clean up the URL after a delay
          setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
        } catch (fallbackError) {
          console.warn('Fallback failed, trying Supabase URL:', fallbackError);
          // Final fallback: open the original Supabase URL
          window.open(termsUrl, '_blank');
        }
      }

      console.log('✅ PDF sharing completed successfully');
    } catch (error) {
      console.error('Error sharing PDF:', error);
      // Final fallback: try to open the original URL
      try {
        window.open(termsUrl, '_blank');
      } catch (finalError) {
        console.error('All sharing methods failed:', finalError);
      }
    }
  };

  // Transform database record to match component interface
  const transformedMedicalRecord = medicalRecord ? {
    id: medicalRecord.id,
    hasPreviewHeartCondition: medicalRecord.has_previous_heart_condition,
    isPregnant: medicalRecord.is_pregnant,
    cardiovascularConditions: medicalRecord.cardiovascular_conditions as string[] || [],
    metabolicConditions: medicalRecord.metabolic_conditions as string[] || [],
    respiratoryConditions: medicalRecord.respiratory_conditions as string[] || [],
    musculoskeletalConditions: medicalRecord.musculoskeletal_conditions as string[] || [],
    otherConditions: medicalRecord.other_conditions as string[] || [],
    thyroidConditions: medicalRecord.thyroid_conditions as string[] || [],
    symptomsAndConditions: medicalRecord.symptoms_and_conditions as Record<string, boolean> || {},
    physicalActivities: medicalRecord.physical_activities as string[] || [],
    additionalNotes: medicalRecord.additional_notes,
    signatureData: medicalRecord.signature_data,
    signatureDate: medicalRecord.signature_date
  } : null;

  const modalContent = (
    <Tabs defaultValue="terms" className="h-full flex flex-col">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="terms" className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          {t('gymSettings.terms')}
        </TabsTrigger>
        <TabsTrigger value="medical" className="flex items-center gap-2">
          <Heart className="h-4 w-4" />
        {t('medicalRecord.title')}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="terms" className="flex-1 min-h-0 mt-4">
        <div className="space-y-4 h-full flex flex-col">
          <div className="flex items-center justify-between flex-shrink-0">
            <h3 className="text-sm font-medium">{t('gymSettings.terms')}</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleSharePDF()}
              className="flex items-center gap-2"
              disabled={!termsUrl}
            >
              <Share className="h-4 w-4" />
              {t('common.share')}
            </Button>
          </div>

          <div className="flex-1 min-h-0">
            <TermsPDFViewer termsUrl={termsUrl} />
          </div>
        </div>
      </TabsContent>

      <TabsContent value="medical" className="flex-1 min-h-0 mt-4">
        <ClientMedicalRecordForm
          clientId={clientId}
          clientName={clientName}
          client={client}
          medicalRecord={transformedMedicalRecord}
          onSave={async record => {
            await saveMedicalRecord(record);
          }}
          onTermsAcceptance={handleTermsAcceptance}
          isSaving={isSaving}
          isRecording={isRecording}
        />
      </TabsContent>
    </Tabs>
  );

  if (isFullScreen) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="h-screen w-full rounded-none border-0 p-0 flex flex-col"
        >
          <SheetHeader className="p-4 border-b flex-shrink-0">
            <SheetTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5" />
              {t('clients.termsAcceptance.signTermsAndConditions')}
            </SheetTitle>
            <SheetDescription>
              {clientName}
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 p-4 overflow-y-auto pb-safe-bottom">
            {modalContent}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className={cn(
      "flex flex-col z-50",
      "max-h-[90vh] max-w-4xl",
    )}>
      <DialogHeader className="p-4 pb-0">
        <DialogTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          {t('clients.termsAcceptance.signTermsAndConditions')} - {clientName}
        </DialogTitle>
      </DialogHeader>

      <div className="flex-1 min-h-0 p-4 pt-0">
        {modalContent}
      </div>
    </DialogContent>
  </Dialog>;
};
export default ClientTermsAcceptanceModal;